import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { CalendarCheck, Filter, Loader2, Check, X, CalendarIcon, RefreshCw, MapPin, Building } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';



const statusLabels = {
  demandee: 'Demandée',
  validee: 'Validée',
  refusee: 'Refusée',
  reprogrammee: 'Reprogrammée',
  realisee: 'Réalisée',
  archivee: 'Archivée'
};

const statusColors = {
  demandee: 'bg-yellow-100 text-yellow-800',
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
  reprogrammee: 'bg-blue-100 text-blue-800',
  realisee: 'bg-purple-100 text-purple-800',
  archivee: 'bg-gray-100 text-gray-800'
};

export default function RendezVous() {
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [visites, setVisites] = useState([]);
  const [sites, setSites] = useState([]);
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisite, setSelectedVisite] = useState(null);
  const [reprogramDialogOpen, setReprogramDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    type_visite: '',
    statut: '',
    date_min: '',
    date_max: ''
  });

  useEffect(() => {
    if (!isAgentOrAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, token]);

  const fetchData = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [visitesRes, sitesRes, logementsRes] = await Promise.all([
        fetch(`${API_URL}/api/visites`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/sites`),
        fetch(`${API_URL}/api/logements`)
      ]);
      
      const visitesData = await visitesRes.json();
      const sitesData = await sitesRes.json();
      const logementsData = await logementsRes.json();
      
      setVisites(visitesData);
      setSites(sitesData);
      setLogements(logementsData);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (visiteId, statut, nouvelleDate = null) => {
    setUpdating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      let url = `${API_URL}/api/visites/${visiteId}/status?statut=${statut}`;
      if (nouvelleDate) {
        url += `&nouvelle_date=${format(nouvelleDate, 'yyyy-MM-dd')}`;
      }
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur');
      
      toast.success(`Visite ${statusLabels[statut].toLowerCase()}`);
      setReprogramDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const getVisiteDetails = (visite) => {
    if (visite.type_visite === 'SITE') {
      const site = sites.find(s => s.site_id === visite.site_id);
      return { type: 'Site', name: site?.nom || site?.ville || 'Non trouvé', icon: MapPin };
    } else {
      const logement = logements.find(l => l.logement_id === visite.logement_id);
      return { type: 'Logement', name: logement?.description?.substring(0, 30) + '...' || 'Non trouvé', icon: Building };
    }
  };

  const filteredVisites = visites.filter(v => {
    if (filters.type_visite && v.type_visite !== filters.type_visite) return false;
    if (filters.statut && v.statut !== filters.statut) return false;
    if (filters.date_min && v.date_visite < filters.date_min) return false;
    if (filters.date_max && v.date_visite > filters.date_max) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="rendez-vous-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-serif mb-2">Rendez-vous & Visites</h1>
          <p className="text-muted-foreground">
            Gérez les demandes de visite pour les sites et logements
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{visites.filter(v => v.statut === 'demandee').length}</div>
              <div className="text-sm text-muted-foreground">En attente</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{visites.filter(v => v.statut === 'validee').length}</div>
              <div className="text-sm text-muted-foreground">Validées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{visites.filter(v => v.statut === 'reprogrammee').length}</div>
              <div className="text-sm text-muted-foreground">Reprogrammées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{visites.filter(v => v.statut === 'realisee').length}</div>
              <div className="text-sm text-muted-foreground">Réalisées</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-8 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Filtres</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select value={filters.type_visite || "all"} onValueChange={(v) => setFilters({ ...filters, type_visite: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-type-visite">
                <SelectValue placeholder="Type de visite" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="SITE">Site</SelectItem>
                <SelectItem value="LOGEMENT">Logement</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.statut || "all"} onValueChange={(v) => setFilters({ ...filters, statut: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-statut">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="demandee">Demandée</SelectItem>
                <SelectItem value="validee">Validée</SelectItem>
                <SelectItem value="refusee">Refusée</SelectItem>
                <SelectItem value="reprogrammee">Reprogrammée</SelectItem>
                <SelectItem value="realisee">Réalisée</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Date min"
              value={filters.date_min}
              onChange={(e) => setFilters({ ...filters, date_min: e.target.value })}
              data-testid="filter-date-min"
            />
            <Input
              type="date"
              placeholder="Date max"
              value={filters.date_max}
              onChange={(e) => setFilters({ ...filters, date_max: e.target.value })}
              data-testid="filter-date-max"
            />
          </div>
        </Card>

        {/* Visites Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des visites ({filteredVisites.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Objet</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVisites.map(visite => {
                    const details = getVisiteDetails(visite);
                    const Icon = details.icon;
                    return (
                      <TableRow key={visite.visite_id} data-testid={`visite-row-${visite.visite_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-primary" />
                            <span>{details.type}</span>
                          </div>
                        </TableCell>
                        <TableCell>{details.name}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{visite.client_nom}</div>
                            <div className="text-xs text-muted-foreground">{visite.client_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>{visite.date_visite}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[visite.statut]}>
                            {statusLabels[visite.statut]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {visite.statut === 'demandee' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                                onClick={() => handleUpdateStatus(visite.visite_id, 'validee')}
                                data-testid={`validate-btn-${visite.visite_id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600"
                                onClick={() => {
                                  setSelectedVisite(visite);
                                  setNewDate(null);
                                  setReprogramDialogOpen(true);
                                }}
                                data-testid={`reprogram-btn-${visite.visite_id}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleUpdateStatus(visite.visite_id, 'refusee')}
                                data-testid={`reject-btn-${visite.visite_id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {visite.statut === 'validee' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateStatus(visite.visite_id, 'realisee')}
                              data-testid={`complete-btn-${visite.visite_id}`}
                            >
                              Marquer réalisée
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {filteredVisites.length === 0 && (
              <div className="text-center py-12">
                <CalendarCheck className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Aucune visite trouvée</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reprogram Dialog */}
        <Dialog open={reprogramDialogOpen} onOpenChange={setReprogramDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reprogrammer la visite</DialogTitle>
              <DialogDescription>
                Sélectionnez une nouvelle date pour cette visite
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Calendar
                mode="single"
                selected={newDate}
                onSelect={setNewDate}
                locale={fr}
                disabled={(date) => date < new Date()}
                className="rounded-md border mx-auto"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReprogramDialogOpen(false)}>Annuler</Button>
              <Button 
                onClick={() => handleUpdateStatus(selectedVisite?.visite_id, 'reprogrammee', newDate)} 
                disabled={updating || !newDate}
              >
                {updating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
